
'use client';

import { motion, useScroll } from 'framer-motion';
import { useState, useEffect } from 'react';

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = useState(false);

  // Update scroll state
  useEffect(() => {
    // Guard against undefined scrollY (should never happen, but keeps TS happy)
    if (!scrollY) return;
    return scrollY.on('change', (latest) => {
      setIsScrolled(latest > 50);
    });
  }, [scrollY]);

  // Prevent body scroll when the mobile menu is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = isOpen ? 'hidden' : originalOverflow;

    // Cleanup on unmount or when isOpen changes
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (!element) {
      console.warn(`Element with id "${id}" not found.`);
      return;
    }

    element.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-black/95 backdrop-blur-md shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-base sm:text-lg md:text-xl lg:text-2xl font-serif text-white tracking-wider cursor-pointer"
            onClick={() => scrollToSection('hero')}
          >
            PISTOL LIGHTER
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-4 lg:gap-8">
            <button
              onClick={() => scrollToSection('details')}
              className="text-white/80 hover:text-white transition-colors text-sm tracking-wide cursor-pointer py-2"
            >
              Details
            </button>
            <button
              onClick={() => scrollToSection('specs')}
              className="text-white/80 hover:text-white transition-colors text-sm tracking-wide cursor-pointer py-2"
            >
              Specs
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="text-white/80 hover:text-white transition-colors text-sm tracking-wide cursor-pointer py-2"
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('offer')}
              className="bg-white text-black px-5 lg:px-6 py-2 rounded-full text-sm font-medium hover:bg-white/90 transition-all whitespace-nowrap cursor-pointer"
            >
              Get Yours
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-white z-50 relative w-10 h-10 flex flex-col justify-center items-center cursor-pointer -mr-2"
            aria-label="Menu"
          >
            <motion.span
              animate={isOpen ? { rotate: 45, y: 0 } : { rotate: 0, y: -6 }}
              className="w-5 h-0.5 bg-white absolute transition-all"
            />
            <motion.span
              animate={isOpen ? { opacity: 0 } : { opacity: 1 }}
              className="w-5 h-0.5 bg-white absolute"
            />
            <motion.span
              animate={isOpen ? { rotate: -45, y: 0 } : { rotate: 0, y: 6 }}
              className="w-5 h-0.5 bg-white absolute transition-all"
            />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Menu Overlay */}
      <motion.div
        initial={{ x: '100%' }}
        animate={{ x: isOpen ? 0 : '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
        className="fixed inset-0 bg-black z-40 md:hidden flex flex-col items-center justify-center gap-6"
      >
        <button
          onClick={() => scrollToSection('details')}
          className="text-white text-xl font-light tracking-wide cursor-pointer py-3 px-8 active:bg-white/10 rounded-lg transition-colors"
        >
          Details
        </button>
        <button
          onClick={() => scrollToSection('specs')}
          className="text-white text-xl font-light tracking-wide cursor-pointer py-3 px-8 active:bg-white/10 rounded-lg transition-colors"
        >
          Specs
        </button>
        <button
          onClick={() => scrollToSection('reviews')}
          className="text-white text-xl font-light tracking-wide cursor-pointer py-3 px-8 active:bg-white/10 rounded-lg transition-colors"
        >
          Reviews
        </button>
        <button
          onClick={() => scrollToSection('offer')}
          className="bg-white text-black px-8 py-3 rounded-full text-base font-medium whitespace-nowrap cursor-pointer active:scale-95 transition-transform mt-4"
        >
          Get Yours
        </button>
      </motion.div>
    </>
  );
}
